import React, { FC, ReactNode } from "react";
import styled, { useTheme } from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { faCircleCheck as faCheck, faPalette, faFileLines } from "@duocards/pro-solid-svg-icons";
import { faCardsBlank } from "@duocards/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSolidMammoth } from "../../../icons/CustomIcons";
import PublicIcon from "@mui/icons-material/Public";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import UndoIcon from "@mui/icons-material/Undo";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import Flames from "components/fire/Flames";
import FilterIcon from "icons/FilterIcon";
import {
  Container,
  Grid,
  Row,
  Label as BaseLabel,
  WhiteCell,
  Cell,
  IconContainer,
  CheckIcon,
  Dash,
  HeaderRow
} from "components/ads/PaywallComparisonGrid.styled";

const FeatureIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.duo.color.primary};
  svg {
    width: 22px !important;
    height: 22px !important;
  }
`;

const StyledFlames = styled(Flames)`
  transform: scale(0.9);
`;

const MammothIcon = styled(FontAwesomeIcon)`
  width: 24px !important;
`;

const StyledFilterIcon = styled(FilterIcon)`
  fill: ${({ theme }) => theme.duo.color.primary};
`;

const Label = styled(BaseLabel)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 12px;
  font-weight: 600;
`;

type Feature = {
  name: string;
  icon: ReactNode;
  free: boolean;
  premium: boolean;
  iconColor?: string;
};

type Props = {
  expanded: boolean;
};

const SubscriptionComparisonGrid: FC<Props> = ({ expanded }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const basicFeatures: Feature[] = [
    {
      name: t("Essential card learning"),
      icon: <FontAwesomeIcon icon={faCardsBlank} />,
      iconColor: theme.duo.color.green,
      free: true,
      premium: true
    },
    {
      name: t("Unlimited experience"),
      icon: <StyledFlames strength={4} xps={null} />,
      free: false,
      premium: true
    },
    {
      name: t("Unlimited languages"),
      icon: <PublicIcon />,
      iconColor: theme.duo.color.green,
      free: false,
      premium: true
    }
  ];

  const expandedFeatures: Feature[] = [
    {
      name: t("100% Ad free"),
      icon: <NotInterestedIcon />,
      iconColor: theme.duo.color.red,
      free: false,
      premium: true
    },
    {
      name: t("Grammar"),
      icon: <MenuBookRoundedIcon />,
      free: false,
      premium: true
    },
    {
      name: t("AI messages"),
      icon: <MammothIcon icon={faSolidMammoth} />,
      free: false,
      premium: true
    },
    {
      name: t("Series freeze"),
      icon: <ViewInArIcon />,
      free: false,
      premium: true
    },
    {
      name: t("Voices"),
      icon: <RecordVoiceOverIcon />,
      free: false,
      premium: true
    },
    {
      name: t("Filters"),
      icon: <StyledFilterIcon />,
      free: false,
      premium: true
    },
    {
      name: t("Undo button"),
      icon: <UndoIcon />,
      free: false,
      premium: true
    },
    {
      name: t("Color themes"),
      icon: <FontAwesomeIcon icon={faPalette} />,
      free: false,
      premium: true
    },
    {
      name: t("Test from known cards"),
      icon: <FontAwesomeIcon icon={faFileLines} />,
      free: false,
      premium: true
    },
    {
      name: t("Priority support"),
      icon: <SupportAgentOutlinedIcon />,
      free: false,
      premium: true
    }
  ];

  const features = expanded ? [...basicFeatures, ...expandedFeatures] : basicFeatures;

  return (
    <Container>
      <Grid>
        <HeaderRow />
      </Grid>
      <Grid>
        {features.map((feature, index) => (
          <Row key={feature.name}>
            <Label>
              <FeatureIcon style={{ color: feature.iconColor }}>{feature.icon}</FeatureIcon>
              {feature.name}
            </Label>
            <Cell>
              {feature.free ? (
                <IconContainer>
                  <CheckIcon icon={faCheck} />
                </IconContainer>
              ) : (
                <Dash />
              )}
            </Cell>
            <WhiteCell>
              <Cell $isPremium $isLast={index === features.length - 1}>
                {feature.premium ? (
                  <IconContainer>
                    <CheckIcon icon={faCheck} $isPremium />
                  </IconContainer>
                ) : (
                  <Dash />
                )}
              </Cell>
            </WhiteCell>
          </Row>
        ))}
      </Grid>
    </Container>
  );
};

export default SubscriptionComparisonGrid;
